/**
 * Keeps signed-in visitors off the signed-out account pages: `/login`, `/register`, and the
 * forgot-password and set-password steps (`pages/login/index.tsx`, `pages/register/index.tsx`,
 * `pages/login/forgot-password/**`, `pages/login/set-password/**`). Each page renders nothing
 * while this returns true.
 *
 * It reads sessionStorage `redirectTo`, the return path in the sign-in hand-off shared with
 * web-store-ui (web-architecture ADR-0035; writers listed in
 * `components/NextLayouts/ProtectedRoute.tsx`). Unlike `LoginForm.tsx` → `onLoginClick`, it
 * leaves the key in place, and its fallback is `/store/dashboard` in production (`/` under
 * `next dev`), where `onLoginClick` always falls back to `/`.
 */
import { navigate } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";

/**
 * When the user is signed in, navigates to sessionStorage `redirectTo`, otherwise to
 * `/store/dashboard` when `NODE_ENV` is `production` and `/` otherwise. `navigate` is a
 * client-side push; in production `pages/_app.tsx` → `App` turns a `/store` target into a full
 * reload. It navigates during render, on every render while signed in.
 * @returns {boolean} - true if user should be redirected, false otherwise
 */
const useRedirectIfLoggedIn = (): boolean => {
	const {
		user: { isLoggedIn },
	} = useApplication();

	if (isLoggedIn) {
		let redirectTo = "";
		if (typeof sessionStorage !== "undefined") {
			const storedRedirect = sessionStorage.getItem("redirectTo");
			redirectTo = (!!storedRedirect && storedRedirect) || "";
		} 
		let defaultDestination = process.env.NODE_ENV === "production" ? "/store/dashboard" : "/"; 
		navigate(redirectTo || defaultDestination);
		return true;
	}

	return false;
};

export default useRedirectIfLoggedIn;
